// AudioWaveformPlayer.js
// Audio player with waveform visualization for call recordings

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, 
  Download, Maximize2, Minimize2, RotateCcw, Clock,
  Phone, PhoneIncoming, PhoneOutgoing, Loader2
} from 'lucide-react';
import { format } from 'date-fns';

// Waveform Canvas Component
const WaveformCanvas = ({ 
  audioData, 
  currentTime, 
  duration, 
  onSeek,
  isPlaying,
  primaryColor = '#7c3aed',
  secondaryColor = '#e2e8f0'
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 80 });
  
  // Generate waveform data if not provided
  const waveformData = useMemo(() => {
    if (audioData && audioData.length > 0) return audioData;
    
    // Generate synthetic waveform based on duration
    const bars = Math.min(Math.max(50, Math.floor(duration * 2)), 200);
    return Array.from({ length: bars }, (_, i) => {
      // Create natural-looking wave pattern
      const base = Math.sin(i * 0.3) * 0.3 + 0.5;
      const variation = Math.random() * 0.4;
      return Math.min(1, Math.max(0.1, base + variation));
    });
  }, [audioData, duration]);
  
  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);
  
  // Draw waveform
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !waveformData.length) return;
    
    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;
    
    // Set canvas size
    canvas.width = width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Calculate bar dimensions
    const barCount = waveformData.length;
    const barWidth = Math.max(2, (width / barCount) * 0.7);
    const barGap = (width / barCount) * 0.3;
    const progressPercent = duration > 0 ? currentTime / duration : 0;
    const progressX = width * progressPercent;
    
    // Draw bars
    waveformData.forEach((amplitude, index) => {
      const x = (index / barCount) * width;
      const barHeight = amplitude * (height * 0.8);
      const y = (height - barHeight) / 2;
      
      // Determine color based on playback position
      const isPlayed = x < progressX;
      
      // Create gradient for played portion
      if (isPlayed) {
        const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
        gradient.addColorStop(0, primaryColor);
        gradient.addColorStop(1, `${primaryColor}aa`);
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = secondaryColor;
      }
      
      // Draw rounded bar
      const radius = barWidth / 2;
      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, radius);
      ctx.fill();
    });
    
    // Draw playhead
    if (isPlaying || currentTime > 0) {
      ctx.fillStyle = primaryColor;
      ctx.fillRect(progressX - 1, 0, 2, height);
    }
    
  }, [waveformData, currentTime, duration, dimensions, isPlaying, primaryColor, secondaryColor]);
  
  // Handle click to seek
  const handleClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !onSeek) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const seekTime = percent * duration;
    onSeek(seekTime);
  };
  
  return (
    <div 
      ref={containerRef} 
      className="w-full h-20 cursor-pointer relative"
      onClick={handleClick}
    >
      <canvas 
        ref={canvasRef} 
        className="w-full h-full"
        style={{ display: 'block' }}
      />
    </div>
  );
};

// Time Display Component
const TimeDisplay = ({ currentTime, duration }) => {
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="flex items-center gap-2 text-sm font-mono">
      <span className="text-foreground">{formatTime(currentTime)}</span>
      <span className="text-muted-foreground">/</span>
      <span className="text-muted-foreground">{formatTime(duration)}</span>
    </div>
  );
};

// Main Audio Waveform Player Component
export const AudioWaveformPlayer = ({ 
  src,
  title,
  subtitle,
  callData,
  onClose,
  compact = false,
  autoPlay = false,
  showDownload = true,
  primaryColor = '#7c3aed'
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [waveformData, setWaveformData] = useState([]);
  
  // Initialize audio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    
    const handleError = (e) => {
      console.error('Audio error:', e);
      setError('Failed to load audio');
      setIsLoading(false);
    };
    
    const handleCanPlay = () => {
      setIsLoading(false);
      if (autoPlay) {
        audio.play().catch(() => {});
        setIsPlaying(true);
      }
    };
    
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    
    // Generate waveform from audio
    generateWaveform(src);
    
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [src, autoPlay]);
  
  // Generate waveform data from audio
  const generateWaveform = async (audioSrc) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const response = await fetch(audioSrc);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const rawData = audioBuffer.getChannelData(0);
      const samples = 100;
      const blockSize = Math.floor(rawData.length / samples);
      const filteredData = [];
      
      for (let i = 0; i < samples; i++) {
        let blockStart = blockSize * i;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[blockStart + j]);
        }
        filteredData.push(sum / blockSize);
      }
      
      // Normalize
      const max = Math.max(...filteredData);
      const normalized = filteredData.map(n => n / max);
      setWaveformData(normalized);
      
      audioContext.close();
    } catch (err) {
      console.log('Could not generate waveform from audio, using synthetic');
    }
  };
  
  // Playback controls
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);
  
  const handleSeek = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(time, duration));
  }, [duration]);
  
  const skipBackward = useCallback(() => {
    handleSeek(currentTime - 10);
  }, [currentTime, handleSeek]);
  
  const skipForward = useCallback(() => {
    handleSeek(currentTime + 10);
  }, [currentTime, handleSeek]);
  
  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);
  
  const handleVolumeChange = useCallback((value) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newVolume = value[0];
    audio.volume = newVolume;
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      audio.muted = false;
      setIsMuted(false);
    }
  }, [isMuted]);
  
  const changePlaybackRate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    audio.playbackRate = nextRate;
    setPlaybackRate(nextRate);
  }, [playbackRate]);
  
  const restart = useCallback(() => {
    handleSeek(0);
    if (!isPlaying) togglePlay();
  }, [handleSeek, isPlaying, togglePlay]);
  
  const handleDownload = useCallback(() => {
    if (!src) return;
    const link = document.createElement('a');
    link.href = src;
    link.download = title || 'recording.mp3';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [src, title]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          skipBackward();
          break;
        case 'ArrowRight':
          skipForward();
          break;
        case 'm':
          toggleMute();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [togglePlay, skipBackward, skipForward, toggleMute]);
  
  if (!src) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 text-center">
          <p className="text-amber-700">No recording available</p>
        </CardContent>
      </Card>
    );
  }
  
  if (compact) {
    return (
      <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
        <audio ref={audioRef} src={src} preload="metadata" />
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={togglePlay}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </Button>
        <div className="flex-1">
          <WaveformCanvas
            audioData={waveformData}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeek}
            isPlaying={isPlaying}
            primaryColor={primaryColor}
          />
        </div>
        <TimeDisplay currentTime={currentTime} duration={duration} />
      </div>
    );
  }
  
  return (
    <Card className="overflow-hidden">
      <audio ref={audioRef} src={src} preload="metadata" />
      
      {/* Header with call info */}
      {(title || callData) && (
        <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-violet-50 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                {callData?.direction === 'inbound' ? (
                  <PhoneIncoming className="h-5 w-5 text-purple-600" />
                ) : (
                  <PhoneOutgoing className="h-5 w-5 text-purple-600" />
                )}
              </div>
              <div>
                <h3 className="font-medium">{title || 'Call Recording'}</h3>
                {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
                {callData && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                    {callData.started_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(callData.started_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    )}
                    {callData.duration_seconds && (
                      <Badge variant="outline" className="text-xs">
                        {Math.floor(callData.duration_seconds / 60)}:{(callData.duration_seconds % 60).toString().padStart(2, '0')}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
            {showDownload && (
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
            )}
          </div>
        </div>
      )}
      
      <CardContent className="p-4 space-y-4">
        {/* Error state */}
        {error && (
          <div className="text-center py-4 text-red-600">
            <p>{error}</p>
          </div>
        )}
        
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            <span className="ml-2 text-muted-foreground">Loading audio...</span>
          </div>
        )}
        
        {/* Waveform */}
        {!error && (
          <WaveformCanvas
            audioData={waveformData}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeek}
            isPlaying={isPlaying}
            primaryColor={primaryColor}
          />
        )}
        
        {/* Controls */}
        <div className="flex items-center justify-between">
          <TimeDisplay currentTime={currentTime} duration={duration} />
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={restart} title="Restart">
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={skipBackward} title="Back 10s">
              <SkipBack className="h-4 w-4" />
            </Button>
            <Button 
              onClick={togglePlay} 
              disabled={isLoading || error}
              className="h-10 w-10 rounded-full bg-purple-600 hover:bg-purple-700"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 text-white" />
              ) : (
                <Play className="h-5 w-5 text-white ml-0.5" />
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={skipForward} title="Forward 10s">
              <SkipForward className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={changePlaybackRate}
              title="Playback speed"
            >
              <span className="text-xs font-medium">{playbackRate}x</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={toggleMute}>
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              value={[isMuted ? 0 : volume]}
              onValueChange={handleVolumeChange}
              max={1}
              step={0.1}
              className="w-20"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Recording Player Dialog for modal use
export const RecordingPlayerDialog = ({ 
  open, 
  onOpenChange, 
  recording,
  callData 
}) => {
  if (!recording) return null;
  
  return (
    <div className={`fixed inset-0 z-50 ${open ? 'block' : 'hidden'}`}>
      <div 
        className="fixed inset-0 bg-black/50" 
        onClick={() => onOpenChange(false)}
      />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl">
        <AudioWaveformPlayer
          src={recording.url || recording.recording_url}
          title={callData?.contact_name || 'Call Recording'}
          subtitle={callData?.to_number || callData?.from_number}
          callData={callData}
          onClose={() => onOpenChange(false)}
        />
      </div>
    </div>
  );
};

export default AudioWaveformPlayer;
